import '@/polyfills/customEvent';
import App from './App.vue';
import store, { key } from './store';
import VueClickAway from './plugins/click-away';
import Toast, { PluginOptions } from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import './globalFormRules';
import routes from './router';
import viteSSR from 'vite-ssr';
import { createHead } from '@vueuse/head';
import { ClientOnly } from 'vite-ssr/vue';
import { Head } from '@vueuse/head';
import { state as defaultUtilityState } from '@/store/modules/utility/state';
import { state as defaultEditingState } from '@/store/modules/editing/state';
import { state as defaultGlobalState } from '@/store/modules/global/state';
import { state as defaultRepeatableState } from '@/store/modules/repeatable/state';
import { state as defaultGamestepsState } from '@/store/modules/gamesteps/state';
import Section from '@/components/layout/section/LFSection.vue';
import { initCookieConsent } from '@/services/cookieConsent';
import Base64 from 'node_modules/js-base64/base64.js';
import Particles from 'vue3-particles';

import '@/services/repeatable';
import '@/services/repeatable/implementations/json';
import '@/services/repeatable/implementations/rss';

// Include our SDK. This will auto-extend window object to contain it.
import '@/sdk';
import { Cache } from '@/services/cache';
import { isSsr } from '@/utilities/Utilities';
import { events } from '@/services/events';
import { State } from '@/store/modules/campaign/state';
import { ClipboardState } from '@/store/modules/clipboard/state';

import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';
import { ConfigData } from './Config';
import initializeSDK from '@/sdk';

export default viteSSR(
  App,
  {
    routes,
    transformState(state) {
      if (isSsr()) {
        // The raw value that we return here is being outputted in the DOM.
        // So we need to wrap it in a JSON.parse() to make it appear as a string in DOM.
        return JSON.stringify(
          Base64.encode(
            JSON.stringify({
              store: {
                campaign: {
                  data: state.store.getters['campaign/data'],
                  demoToken: state.store.getters['campaign/demoToken']
                },
                editing: defaultEditingState(),
                utility: defaultUtilityState(),
                global: defaultGlobalState(),
                repeatable: defaultRepeatableState(),
                gamesteps: defaultGamestepsState(),
                clipboard: {}
              }
            })
          )
        );
      } else {
        // Undefined when using yarn dev
        if (!state) {
          return {};
        }

        return JSON.parse(Base64.decode(state ?? ''));
      }
    }
  },
  (context) => {
    const { app, router, initialState, request, initialRoute } = context;
    if (ConfigData.sentryDsn) {
      const hostName =
        request?.headers['x-forwarded-host'] ??
        request?.headers['host'] ??
        (typeof window !== 'undefined' ? window.location.host : '');

      Sentry.init({
        app,
        dsn: ConfigData.sentryDsn,
        integrations: !isSsr()
          ? [
              new Integrations.BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: ['appv2.leadfamly.com', 'qav2.leadfamly.com']
              })
            ]
          : [],
        tracesSampleRate: hostName === 'qav2.leadfamly.com' ? 1.0 : 0,
        beforeSend(event) {
          return event;
        }
      });
    }

    const head = createHead();

    // Flush cookie consent callbacks & expose it to the window object
    initCookieConsent();

    // Clear the internal caching between requests so we don't re-use it cross-requests.
    Cache.clear();

    // Clear all event listeners so that we get a fresh bootstrap
    events.removeAllListeners();

    // If we're in a server context we need to ensure that we replace the entire state with a base state.
    // This is to ensure that between requests that server doesn't utilize the same store as a previous request.
    if (isSsr()) {
      const host = request?.headers['x-forwarded-host'] ?? request?.headers['host'] ?? '';
      // Write in server
      store.replaceState({
        // In server context we forward some express information. This is because window object isn't available &
        // thus we need to forward the host, userAgent & path for the underlying application.
        utility: {
          ...defaultUtilityState(),
          userAgent: request?.headers['user-agent'] ?? '',
          host: Array.isArray(host) ? host[0] : host,
          cookies: request?.headers['cookie'] ?? request?.headers['cookie'] ?? '',
          // request.path is incorrectly typed by the library. .path is in fact accessible on the NodeJS server.
          // @ts-ignore
          path: request?.path ?? null,
          initialUrlHash: initialRoute.hash ?? null
        },
        campaign: {} as State,
        editing: defaultEditingState(),
        global: defaultGlobalState(),
        repeatable: defaultRepeatableState(),
        gamesteps: defaultGamestepsState(),
        clipboard: {} as ClipboardState
      });
      initialState.store = store;
    } else {
      // Read in browser
      if (initialState.store?.campaign?.data) {
        store.replaceState(initialState.store);
        store.state.utility.initialUrlHash = window.location.hash;
      } else {
        store.replaceState({
          utility: defaultUtilityState(),
          campaign: {} as State,
          editing: defaultEditingState(),
          global: defaultGlobalState(),
          repeatable: defaultRepeatableState(),
          gamesteps: defaultGamestepsState(),
          clipboard: {} as ClipboardState
        });
      }
    }

    // Provide the initial state to the application. This state is then parsed into our store so that for SSR
    // we re-use the state that was generated in Node.
    app.provide('initialState', initialState);

    // Attach our head store. This ensures we don't re-use heads from a previous request.
    app.use(head);

    //Allow particles to be used in app
    app.use(Particles);

    // Bind the store using a injection key. This will make typings work when we use the useStore() hook in
    // composition API.
    app.use(store, key);

    // Custom component copied from the official NPM package. This was to fix SSR compatability.
    app.use(VueClickAway);

    const options: PluginOptions = {
      toastClassName: 'lf-toast',
      bodyClassName: 'lf-toast__body',
      closeButtonClassName: 'lf-toast__close',
      icon: false,
      hideProgressBar: true,
      timeout: false
    };
    app.use(Toast, options);

    // <Head /> component allows our components to inject content into the <head> tag. Both on the server & client.
    app.component(Head.name, Head);

    // <ClientOnly /> component will only render the content of the component if we're in client context.
    app.component(ClientOnly.name, ClientOnly);

    // Make our <Section /> component globally available. This will also make it available in recursive inclusion.
    app.component(Section.name, Section);

    // @ts-ignore // We need to find a way to type this
    const isDevEnv = (import.meta as unknown).env.MODE === 'development';

    if (!isDevEnv) {
      initializeSDK();
    }

    return { head };
  }
);
